import React, { useEffect, useState } from 'react';

import { definitions } from '__generated__/openapi-specs/ProductEligibilityService';
import {
  MobilePlansDetails,
  OfferEligibilityLevelEnum,
  SelectedPlan,
} from 'components/shop/MobilePlans/typings/plans';
import { useAddToCart } from '../../hooks/useAddToCart/useAddToCart';
import EligibilityChecks from '../EligibilityChecks';
import PrimaryPlanStep from '../LeaderPlanSteps/PrimaryPlanStep';
import SelectQuantityStep from '../SelectQuantityStep';

interface PlanConfigurationProps {
  selectedPlan: SelectedPlan;
  planCardsProducts: MobilePlansDetails;
  setLayerTitle: (title: string) => void;
  onCancel(): void;
  scrollToTop(): void;
}

export type PlanConfigurationStep =
  | 'SELECT_QUANTITY'
  | 'SELECT_ACCOUNT'
  | 'ADD_RELATED_OFFERS'
  | 'ADD_COMPANION_PLAN_ONLY'
  | 'SHOW_ELIGIBLE_MESSAGE'
  | 'SELECT_QUANTITY_BEFORE_ELIGIBLE_MESSAGE'
  | 'SELECT_KIDS_PLAN_QUANTITY'
  | 'SELECT_COMPANION_QUANTITY'
  | 'REVIEW_RELATED_OFFERS_SELECTION';

const PlanConfiguration = ({
  selectedPlan: {
    relatedOfferId,
    relatedOfferIds,
    offerEligibilityLevel,
    ...selectedPlan
  },
  planCardsProducts,
  setLayerTitle,
  onCancel,
  scrollToTop,
}: PlanConfigurationProps) => {
  const getOfferIds = () => {
    if (relatedOfferIds) {
      return relatedOfferIds;
    }
    return relatedOfferId ? [relatedOfferId] : [];
  };
  const offerIds = getOfferIds();
  const hasRelatedOffers = offerIds.length > 0;

  const getInitialStep = () => {
    if (offerEligibilityLevel === OfferEligibilityLevelEnum.Account)
      return 'SELECT_ACCOUNT';
    if (hasRelatedOffers) return 'ADD_RELATED_OFFERS';
    return 'SELECT_QUANTITY';
  };
  const [step, setStep] = useState<PlanConfigurationStep>(getInitialStep());
  const [selectedAccount, setSelectedAccount] =
    useState<definitions['EligibleAccount']>();

  useEffect(() => {
    scrollToTop();
  }, [step, scrollToTop]);

  const { isAddingBundles, addToCart } = useAddToCart(planCardsProducts);

  const addSelectedPlanToCart = () =>
    addToCart({
      accountNumber: selectedAccount?.accountNumber,
      plans: [selectedPlan],
    });

  if (step === 'SELECT_ACCOUNT') {
    setLayerTitle('Account selection');
    return (
      <EligibilityChecks
        selectedPlan={selectedPlan}
        navigate={setStep}
        setSelectedAccount={setSelectedAccount}
      />
    );
  }

  if (step === 'SELECT_QUANTITY') {
    const { eligibilityCountAvailable, eligibilityCountMaximum } =
      selectedAccount || {};

    const eligibilityCount =
      eligibilityCountAvailable || eligibilityCountMaximum
        ? {
            available: eligibilityCountAvailable,
            maximum: eligibilityCountMaximum,
          }
        : undefined;

    const nextButtonText = eligibilityCount ? 'Next' : 'Add to cart';

    setLayerTitle('Selected plan');

    return (
      <SelectQuantityStep
        planCardsProducts={planCardsProducts}
        selectedPlan={selectedPlan}
        isAddingBundles={isAddingBundles}
        onNext={addSelectedPlanToCart}
        onCancel={onCancel}
        nextButtonText={nextButtonText}
        eligibilityCount={eligibilityCount}
      />
    );
  }

  if (hasRelatedOffers) {
    return (
      <PrimaryPlanStep
        onCancel={onCancel}
        setLayerTitle={setLayerTitle}
        step={step}
        selectedPlan={selectedPlan}
        relatedOfferIds={offerIds}
        planCardsProducts={planCardsProducts}
        isAddingBundles={isAddingBundles}
        navigate={setStep}
        addSelectedPlanToCart={addSelectedPlanToCart}
      />
    );
  }

  return null;
};

export default PlanConfiguration;
